package redmcrosco.alex.farmatch.ui

import farmatch.composeapp.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.lerp
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import redmcrosco.alex.farmatch.models.*
import redmcrosco.alex.farmatch.utils.TimeUtils
import redmcrosco.alex.farmatch.voice.VoiceIntent
import kotlinx.datetime.*
import kotlinx.coroutines.launch

@Composable
fun HorizontalCalendar(
    selectedDates: List<Long>,
    onDateSelected: (Long) -> Unit,
    isRange: Boolean = false,
    rangeStart: Long? = null,
    rangeEnd: Long? = null,
    onRangeSelected: (Long?, Long?) -> Unit = { _, _ -> }
) {
    val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
    val currentMonth = now.month
    val currentYear = now.year
    
    val pagerState = rememberPagerState(pageCount = { 12 })
    val scope = rememberCoroutineScope()

    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(
                onClick = { scope.launch { if (pagerState.currentPage > 0) pagerState.animateScrollToPage(pagerState.currentPage - 1) } },
                enabled = pagerState.currentPage > 0
            ) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null, modifier = Modifier.size(20.dp))
            }
            
            val displayDate = remember(pagerState.currentPage) {
                val totalMonths = (currentYear * 12 + currentMonth.number - 1) + pagerState.currentPage
                val year = totalMonths / 12
                val monthIdx = (totalMonths % 12) + 1
                val monthName = when(monthIdx) {
                    1 -> "Gennaio"; 2 -> "Febbraio"; 3 -> "Marzo"; 4 -> "Aprile"; 5 -> "Maggio"; 6 -> "Giugno"
                    7 -> "Luglio"; 8 -> "Agosto"; 9 -> "Settembre"; 10 -> "Ottobre"; 11 -> "Novembre"; 12 -> "Dicembre"
                    else -> ""
                }
                "$monthName $year"
            }
            
            Text(displayDate, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyLarge)
            
            IconButton(
                onClick = { scope.launch { if (pagerState.currentPage < 11) pagerState.animateScrollToPage(pagerState.currentPage + 1) } },
                enabled = pagerState.currentPage < 11
            ) {
                Icon(Icons.AutoMirrored.Filled.ArrowForward, contentDescription = null, modifier = Modifier.size(20.dp))
            }
        }
        
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.fillMaxWidth().height(280.dp)
        ) { page ->
            val totalMonths = (currentYear * 12 + currentMonth.number - 1) + page
            val year = totalMonths / 12
            val month = Month(totalMonths % 12 + 1)
            
            MonthView(
                year = year,
                month = month,
                selectedDates = selectedDates,
                onDateClick = { millis ->
                    if (isRange) {
                        if (rangeStart == null || (rangeStart != null && rangeEnd != null)) {
                            onRangeSelected(millis, null)
                        } else {
                            if (millis < rangeStart) onRangeSelected(millis, null)
                            else onRangeSelected(rangeStart, millis)
                        }
                    } else {
                        onDateSelected(millis)
                    }
                },
                rangeStart = rangeStart,
                rangeEnd = rangeEnd
            )
        }
    }
}

private fun getMonthLength(month: Month, year: Int): Int {
    val isLeap = (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0))
    return when (month) {
        Month.FEBRUARY -> if (isLeap) 29 else 28
        Month.APRIL, Month.JUNE, Month.SEPTEMBER, Month.NOVEMBER -> 30
        else -> 31
    }
}

@Composable
fun MonthView(
    year: Int,
    month: Month,
    selectedDates: List<Long>,
    onDateClick: (Long) -> Unit,
    rangeStart: Long? = null,
    rangeEnd: Long? = null
) {
    val firstDayOfMonth = LocalDate(year, month, 1)
    val daysInMonth = getMonthLength(month, year)
    // Allineamento Lunedì = 0
    val firstDayOfWeek = (firstDayOfMonth.dayOfWeek.ordinal) 
    
    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp)) {
        Row(modifier = Modifier.fillMaxWidth()) {
            listOf("L", "M", "M", "G", "V", "S", "D").forEach { day ->
                Text(
                    text = day,
                    modifier = Modifier.weight(1f),
                    style = MaterialTheme.typography.labelSmall,
                    color = Color.Gray,
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
            }
        }
        
        Spacer(modifier = Modifier.height(4.dp))
        
        var currentDay = 1
        for (row in 0..5) {
            Row(modifier = Modifier.fillMaxWidth()) {
                for (col in 0..6) {
                    val dayIdx = row * 7 + col
                    if (dayIdx < firstDayOfWeek || currentDay > daysInMonth) {
                        Box(modifier = Modifier.weight(1f).aspectRatio(1.2f))
                    } else {
                        val date = LocalDate(year, month, currentDay)
                        val millis = date.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                        val isSelected = selectedDates.contains(millis) || millis == rangeStart || millis == rangeEnd
                        val isInRange = rangeStart != null && rangeEnd != null && millis > rangeStart && millis < rangeEnd
                        val isFestivo = TimeUtils.isFestivo(date)
                        
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .aspectRatio(1.2f)
                                .padding(1.dp)
                                .background(
                                    color = when {
                                        isSelected -> MaterialTheme.colorScheme.primary
                                        isInRange -> MaterialTheme.colorScheme.primary.copy(alpha = 0.15f)
                                        else -> Color.Transparent
                                    },
                                    shape = RoundedCornerShape(4.dp)
                                )
                                .clickable { onDateClick(millis) },
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = currentDay.toString(),
                                color = when {
                                    isSelected -> Color.White
                                    isFestivo -> Color.Red
                                    else -> Color.Black
                                },
                                fontWeight = if (isSelected || isFestivo) FontWeight.Bold else FontWeight.Normal,
                                fontSize = 11.sp
                            )
                        }
                        currentDay++
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditableDropdownField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    options: List<String>,
    modifier: Modifier = Modifier,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default
) {
    var expanded by remember { mutableStateOf(false) }
    ExposedDropdownMenuBox(
        expanded = expanded,
        onExpandedChange = { expanded = !expanded },
        modifier = modifier
    ) {
        OutlinedTextField(
            value = value,
            onValueChange = onValueChange,
            label = { Text(label) },
            modifier = Modifier.fillMaxWidth().menuAnchor(MenuAnchorType.PrimaryEditable, true),
            keyboardOptions = keyboardOptions,
            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = expanded) },
            shape = RoundedCornerShape(12.dp)
        )
        if (options.isNotEmpty()) {
            ExposedDropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option) },
                        onClick = { onValueChange(option); expanded = false }
                    )
                }
            }
        }
    }
}

/**
 * Azione identificata a voce ma non ancora eseguita: serve un [VoiceIntent.Conferma] esplicito
 * successivo perché venga davvero eseguita. Un solo pulsante microfono su questa schermata copre
 * tutto (crea, pubblica, cancella, modifica): questo stato tiene traccia di QUALE azione è in
 * sospeso in un dato momento, qualunque essa sia.
 */
private sealed class AzionePendente {
    object Pubblica : AzionePendente()
    data class Cancella(val ids: List<String>) : AzionePendente()
    data class Modifica(val modificato: AnnuncioTurno) : AzionePendente()
}

private fun turnoCorrisponde(annuncio: AnnuncioTurno, gestisci: VoiceIntent.GestisciAnnunci): Boolean {
    val data = annuncio.dataTurno.toLocalDateTime(TimeZone.UTC).date
    val criterioInizio = gestisci.data
    val criterioFine = gestisci.dataFine
    val dataOk = when {
        criterioInizio != null && criterioFine != null -> data in criterioInizio..criterioFine
        criterioInizio != null -> data == criterioInizio
        gestisci.tutti -> true
        else -> false
    }
    val giornoOk = gestisci.giorniSettimana.isEmpty() || data.dayOfWeek in gestisci.giorniSettimana
    return dataOk && giornoOk
}

@OptIn(androidx.compose.foundation.ExperimentalFoundationApi::class)
@Composable
fun DisponibilitaScreen(viewModel: AuthViewModel) {
    val scrollState = rememberScrollState()
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val annunciCaricati = remember { mutableStateListOf<AnnuncioTurno>() }
    val selectedAnnunci = remember { mutableStateListOf<String>() }
    var annuncioDaEditare by remember { mutableStateOf<AnnuncioTurno?>(null) }
    var showDeleteBatchConfirm by remember { mutableStateOf(false) }
    var azionePendente by remember { mutableStateOf<AzionePendente?>(null) }
    val role = viewModel.currentUserRole
    val themeColor = Color(viewModel.selectedThemeColor.hex.removePrefix("0x").toLong(16))

    // Stato del form di creazione turno, sollevato qui (invece che dentro
    // FarmacistaAnnuncioContent/FarmaciaAnnuncioContent) perché l'UNICO pulsante microfono di
    // questa schermata deve poterlo compilare, indipendentemente da dove si trova a scorrere.
    val selectedDates = remember { mutableStateListOf<Long>() }
    var oraInizio by remember { mutableStateOf("09:00") }
    var oraFine by remember { mutableStateOf("13:00") }
    var compenso by remember { mutableStateOf("25,00") }
    var pharmacistsNeeded by remember { mutableStateOf("1") }

    fun loadAnnunci() {
        scope.launch {
            val list = viewModel.getAnnunciUtente()
            annunciCaricati.clear()
            annunciCaricati.addAll(list.sortedBy { it.dataTurno })
        }
    }
    LaunchedEffect(Unit) { loadAnnunci() }

    fun pubblica() {
        if (selectedDates.isEmpty()) return
        val isFarmacista = role == UserRole.FARMACISTA
        val template = AnnuncioTurno(
            creatoDa = if (isFarmacista) CreatoreTipo.FARMACISTA else CreatoreTipo.FARMACIA,
            creatoreId = viewModel.currentUserEmail ?: "",
            tipo = if (isFarmacista) AnnuncioTipo.CERCO_LAVORO else AnnuncioTipo.OFFRO_LAVORO,
            titolo = if (isFarmacista) "Disponibilità Farmacista" else "Ricerca Farmacista",
            dataTurno = Instant.fromEpochMilliseconds(selectedDates.first()),
            oraInizio = oraInizio, oraFine = oraFine, compenso = TimeUtils.parseCurrency(compenso),
            quantitaMassima = if (isFarmacista) 1 else (pharmacistsNeeded.toIntOrNull() ?: 1)
        )
        val batchRequest = BatchAnnuncioRequest(template = template, entries = selectedDates.map {
            BatchEntry(dataTurno = Instant.fromEpochMilliseconds(it), oraInizio = oraInizio, oraFine = oraFine, compenso = TimeUtils.parseCurrency(compenso))
        })
        val todayStart = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.atStartOfDayIn(TimeZone.currentSystemDefault())
        if (selectedDates.any { it < todayStart.toEpochMilliseconds() }) {
            scope.launch { snackbarHostState.showSnackbar("Errore: Non puoi pubblicare turni per date passate.") }
            return
        }
        viewModel.pubblicaBatchAnnunci(batchRequest) { result ->
            val msg = when (result) {
                "SUCCESS" -> { selectedDates.clear(); if (isFarmacista) "Disponibilità pubblicate!" else "Richieste pubblicate!" }
                "CONFLICT" -> "Attenzione: Alcuni turni erano già presenti."
                else -> "Errore durante il salvataggio."
            }
            scope.launch { snackbarHostState.showSnackbar(msg) }
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = Color.Transparent
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding).padding(16.dp).verticalScroll(scrollState)) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = lerp(Color.White, themeColor, 0.15f)),
                shape = RoundedCornerShape(16.dp),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
            ) {
                Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
                    Box(modifier = Modifier.width(6.dp).fillMaxHeight().background(if (themeColor == Color.White) Color.Gray else themeColor))
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(text = stringResource(Res.string.tab_ads), style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold), color = if (themeColor == Color.White) Color.Black else themeColor)
                        Spacer(modifier = Modifier.height(16.dp))
                        ReputationCardContent(viewModel.userReputation, themeColor)
                    }
                }
            }
            Spacer(modifier = Modifier.height(16.dp))

            // UNICO pulsante microfono per l'intera schermata: crea, pubblica, cancella e
            // modifica sono tutti riconosciuti dallo stesso comando, in base a cosa dici.
            VoiceSearchButton(
                hint = "Parla per creare, pubblicare, cancellare o modificare un turno (es. \"turno domani dalle 8 alle 20, 25 euro\", \"pubblica\", \"cancella il turno del 15 ottobre\", \"conferma\")",
                onIntent = { text, intent ->
                    when (intent) {
                        is VoiceIntent.CreaTurno -> {
                            val inizio = intent.data
                            val fine = intent.dataFine
                            val esplicite = intent.dateEsplicite
                            var generateCount = 0
                            var truncated = false
                            if (esplicite != null) {
                                val filtrate = if (intent.giorniSettimana.isEmpty()) esplicite
                                    else esplicite.filter { it.dayOfWeek in intent.giorniSettimana }
                                val daAggiungere = filtrate.take(MAX_DATE_VOCALI)
                                truncated = filtrate.size > daAggiungere.size
                                daAggiungere.forEach { d ->
                                    val millis = d.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                    if (!selectedDates.contains(millis)) { selectedDates.add(millis); generateCount++ }
                                }
                            } else if (inizio != null && fine != null) {
                                val tutte = TimeUtils.generateDatesInRange(inizio, fine, intent.giorniSettimana)
                                val daAggiungere = tutte.take(MAX_DATE_VOCALI)
                                truncated = tutte.size > daAggiungere.size
                                daAggiungere.forEach { d ->
                                    val millis = d.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                    if (!selectedDates.contains(millis)) { selectedDates.add(millis); generateCount++ }
                                }
                            } else if (inizio != null) {
                                val millis = inizio.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                if (!selectedDates.contains(millis)) { selectedDates.add(millis); generateCount++ }
                            }
                            intent.oraInizio?.let { oraInizio = it }
                            intent.oraFine?.let { oraFine = it }
                            intent.compenso?.let { compenso = TimeUtils.formatCurrency(it) }
                            azionePendente = AzionePendente.Pubblica
                            var msg = when {
                                truncated -> "Ho sentito: \"$text\". Intervallo molto lungo, ho selezionato le prime $generateCount date. Controlla e di' \"conferma\" per pubblicare."
                                fine != null || esplicite != null -> "Ho sentito: \"$text\". $generateCount date selezionate, controlla e di' \"conferma\" per pubblicare."
                                else -> "Ho sentito: \"$text\". Controlla i campi e di' \"conferma\" per pubblicare."
                            }
                            if (intent.compenso == null && intent.oraFine?.endsWith(":00") == false) {
                                msg += " Nota: se avevi detto anche l'importo, potrebbe essere finito per sbaglio nei minuti dell'orario di fine (${intent.oraFine}) — controlla il campo Compenso."
                            }
                            msg
                        }
                        is VoiceIntent.GestisciAnnunci -> {
                            val corrispondenti = annunciCaricati.filter { turnoCorrisponde(it, intent) }
                            when (intent.azione) {
                                VoiceIntent.AzioneAnnunci.CANCELLA -> when {
                                    corrispondenti.isEmpty() -> {
                                        azionePendente = null
                                        "Ho sentito: \"$text\". Nessun turno trovato con questi criteri."
                                    }
                                    else -> {
                                        val ids = corrispondenti.mapNotNull { it.id }
                                        selectedAnnunci.clear()
                                        selectedAnnunci.addAll(ids)
                                        azionePendente = AzionePendente.Cancella(ids)
                                        "Ho sentito: \"$text\". Trovati ${corrispondenti.size} turni, selezionati qui sotto — controllali e di' \"conferma\" per cancellarli (oppure usa il pulsante Elimina)."
                                    }
                                }
                                VoiceIntent.AzioneAnnunci.MODIFICA -> when {
                                    corrispondenti.isEmpty() -> {
                                        azionePendente = null
                                        "Ho sentito: \"$text\". Nessun turno trovato con questi criteri."
                                    }
                                    corrispondenti.size > 1 -> {
                                        azionePendente = null
                                        "Ho sentito: \"$text\". Ho trovato ${corrispondenti.size} turni corrispondenti: la modifica a voce funziona solo su un turno alla volta, specifica meglio la data."
                                    }
                                    else -> {
                                        val originale = corrispondenti.first()
                                        val dataLocale = originale.dataTurno.toLocalDateTime(TimeZone.UTC).date
                                        val nuovoOraInizio = intent.nuovoOraInizio ?: originale.oraInizio
                                        val modificato = originale.copy(
                                            oraInizio = nuovoOraInizio,
                                            oraFine = intent.nuovoOraFine ?: originale.oraFine,
                                            compenso = intent.nuovoCompenso ?: originale.compenso,
                                            tariffaTipo = TimeUtils.calculateTariffa(dataLocale, nuovoOraInizio)
                                        )
                                        azionePendente = AzionePendente.Modifica(modificato)
                                        annuncioDaEditare = modificato
                                        "Ho sentito: \"$text\". Turno del ${TimeUtils.formatDate(originale.dataTurno)} pronto con le modifiche: controlla e premi Salva, oppure di' \"conferma\"."
                                    }
                                }
                            }
                        }
                        is VoiceIntent.Conferma -> when (val azione = azionePendente) {
                            AzionePendente.Pubblica -> {
                                if (selectedDates.isEmpty()) {
                                    "Ho sentito: \"$text\". Non c'è ancora nessuna data pronta da pubblicare: crea prima un turno."
                                } else {
                                    val count = selectedDates.size
                                    pubblica()
                                    azionePendente = null
                                    "Ho sentito: \"$text\". Pubblico $count date..."
                                }
                            }
                            is AzionePendente.Cancella -> {
                                viewModel.eliminaBatchAnnunci(azione.ids) { success ->
                                    if (success) {
                                        annunciCaricati.removeAll { it.id in azione.ids }
                                        selectedAnnunci.clear()
                                        scope.launch { snackbarHostState.showSnackbar("Annunci eliminati") }
                                    }
                                }
                                azionePendente = null
                                "Ho sentito: \"$text\". Eliminazione in corso..."
                            }
                            is AzionePendente.Modifica -> {
                                viewModel.updateAnnuncio(azione.modificato) { success ->
                                    if (success) { loadAnnunci(); annuncioDaEditare = null }
                                }
                                azionePendente = null
                                "Ho sentito: \"$text\". Salvataggio in corso..."
                            }
                            null -> "Ho sentito: \"$text\". Non c'è nessuna azione in sospeso da confermare."
                        }
                        else -> "Ho sentito: \"$text\". Non l'ho capito, riprova."
                    }
                },
                onError = {}
            )
            Spacer(modifier = Modifier.height(16.dp))

            if (role == UserRole.FARMACISTA) {
                FarmacistaAnnuncioContent(
                    viewModel, selectedDates, oraInizio, { oraInizio = it }, oraFine, { oraFine = it }, compenso, { compenso = it },
                    onPubblica = { pubblica() }
                )
            } else {
                FarmaciaAnnuncioContent(
                    viewModel, selectedDates, oraInizio, { oraInizio = it }, oraFine, { oraFine = it }, compenso, { compenso = it },
                    pharmacistsNeeded, { pharmacistsNeeded = it },
                    onPubblica = { pubblica() }
                )
            }
            Spacer(modifier = Modifier.height(24.dp))
            HorizontalDivider()
            Spacer(modifier = Modifier.height(16.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(text = stringResource(Res.string.your_active_ads), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                if (selectedAnnunci.isNotEmpty()) {
                    TextButton(onClick = { showDeleteBatchConfirm = true }) {
                        Text(text = "ELIMINA SELEZIONATI (${selectedAnnunci.size})", color = Color.Red, style = MaterialTheme.typography.labelSmall)
                    }
                }
            }
            Spacer(modifier = Modifier.height(12.dp))
            if (annunciCaricati.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().height(100.dp), contentAlignment = Alignment.Center) { Text(stringResource(Res.string.no_ads_inserted), color = Color.Gray) }
            } else {
                annunciCaricati.groupBy { TimeUtils.getMonthYear(it.dataTurno) }.forEach { (monthYear, ads) ->
                    Text(text = monthYear.uppercase(), style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.ExtraBold, color = themeColor, modifier = Modifier.padding(top = 16.dp, bottom = 8.dp, start = 4.dp))
                    ads.forEach { annuncio ->
                        val isSelected = selectedAnnunci.contains(annuncio.id)
                        Card(
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp).clickable {
                                if (selectedAnnunci.isEmpty()) annuncioDaEditare = annuncio
                                else { if (isSelected) selectedAnnunci.remove(annuncio.id) else selectedAnnunci.add(annuncio.id ?: "") }
                            },
                            colors = CardDefaults.cardColors(containerColor = if (isSelected) Color(0xFFFFF9C4) else Color.White),
                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(checked = isSelected, onCheckedChange = { if (it) selectedAnnunci.add(annuncio.id ?: "") else selectedAnnunci.remove(annuncio.id) })
                                Column(modifier = Modifier.weight(1f)) {
                                    Text(text = TimeUtils.formatDate(annuncio.dataTurno), fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyLarge)
                                    Text(text = "${annuncio.oraInizio} - ${annuncio.oraFine}", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
                                }
                                Column(horizontalAlignment = Alignment.End) {
                                    Text(text = "${TimeUtils.formatCurrency(annuncio.compenso ?: 0.0)} €", fontWeight = FontWeight.ExtraBold, color = themeColor)
                                    Text(text = annuncio.tariffaTipo.name.replace("_", " "), style = MaterialTheme.typography.labelSmall, color = Color.Gray)
                                }
                            }
                        }
                    }
                }
            }
        }

        if (selectedAnnunci.isNotEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.BottomCenter) {
                Surface(modifier = Modifier.fillMaxWidth().padding(16.dp), color = Color.White, tonalElevation = 8.dp, shadowElevation = 8.dp, shape = RoundedCornerShape(24.dp), border = androidx.compose.foundation.BorderStroke(1.dp, Color.LightGray.copy(alpha = 0.5f))) {
                    Row(modifier = Modifier.padding(horizontal = 24.dp, vertical = 8.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                        Text("Selezionati: ${selectedAnnunci.size}", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                        Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                            if (selectedAnnunci.size == 1) {
                                IconButton(onClick = { annuncioDaEditare = annunciCaricati.find { it.id == selectedAnnunci.first() } }) { Icon(Icons.Default.Edit, "Modifica", tint = themeColor) }
                            }
                            IconButton(onClick = { showDeleteBatchConfirm = true }) { Icon(Icons.Default.Delete, "Elimina", tint = Color.Red) }
                        }
                    }
                }
            }
        }
    }

    if (showDeleteBatchConfirm) {
        AlertDialog(
            onDismissRequest = { showDeleteBatchConfirm = false },
            title = { Text("Elimina Annunci") },
            text = { Text("Vuoi davvero eliminare gli annunci selezionati (${selectedAnnunci.size})? L'azione non può essere annullata.") },
            confirmButton = {
                Button(onClick = {
                    viewModel.eliminaBatchAnnunci(selectedAnnunci.toList()) { success ->
                        if (success) { annunciCaricati.removeAll { it.id in selectedAnnunci }; selectedAnnunci.clear(); scope.launch { snackbarHostState.showSnackbar("Annunci eliminati") } }
                    }
                    showDeleteBatchConfirm = false
                }, colors = ButtonDefaults.buttonColors(containerColor = Color.Red)) { Text("ELIMINA") }
            },
            dismissButton = { TextButton(onClick = { showDeleteBatchConfirm = false }) { Text("ANNULLA") } }
        )
    }
    
    annuncioDaEditare?.let { ann ->
        EditAnnuncioDialog(annuncio = ann, onDismiss = { annuncioDaEditare = null }, onSave = { updated ->
            viewModel.updateAnnuncio(updated) { success -> if (success) { loadAnnunci(); annuncioDaEditare = null } }
        })
    }
}

@Composable
fun ReputationCardContent(reputation: UserReputation, themeColor: Color) {
    Column {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Column {
                Text(text = stringResource(Res.string.progress_title), style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
                if (reputation.hasEnoughData) StarRating(reputation.stars)
                else Text(text = stringResource(Res.string.reputation_insufficient_data, 5), style = MaterialTheme.typography.bodySmall, color = Color.Gray)
            }
            if (reputation.discountEligible) {
                Surface(color = Color(0xFF4CAF50), shape = RoundedCornerShape(16.dp)) {
                    Text(text = stringResource(Res.string.discount_active_badge), modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp), color = Color.White, style = MaterialTheme.typography.labelSmall, fontWeight = FontWeight.Bold)
                }
            }
        }
        if (!reputation.discountEligible && reputation.nextDiscountProgress > 0) {
            Spacer(modifier = Modifier.height(8.dp))
            LinearProgressIndicator(progress = { (15f - reputation.nextDiscountProgress) / 15f }, modifier = Modifier.fillMaxWidth().height(8.dp), color = themeColor, trackColor = Color.LightGray.copy(alpha = 0.3f), strokeCap = androidx.compose.ui.graphics.StrokeCap.Round)
            Text(text = stringResource(Res.string.discount_progress_label, reputation.nextDiscountProgress), style = MaterialTheme.typography.bodySmall, modifier = Modifier.padding(top = 4.dp))
        }
    }
}

@Composable
fun StarRating(rating: Double) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        repeat(5) { index ->
            Icon(imageVector = AppIcons.Star, contentDescription = null, tint = if (rating >= (index + 1)) Color(0xFFFFB300) else Color.LightGray, modifier = Modifier.size(20.dp))
        }
        Text(text = " (${((rating * 10).toInt().toDouble() / 10.0)})", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, modifier = Modifier.padding(start = 4.dp))
    }
}

/** Tetto di sicurezza: un comando vocale frainteso (es. "tutto l'anno") non deve poter riempire
 *  il form con centinaia di date in un colpo solo — l'utente le vede comunque tutte prima di
 *  pubblicare, ma una lista più corta resta controllabile davvero. */
private const val MAX_DATE_VOCALI = 90

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun FarmacistaAnnuncioContent(
    viewModel: AuthViewModel,
    selectedDates: androidx.compose.runtime.snapshots.SnapshotStateList<Long>,
    oraInizio: String, onOraInizioChange: (String) -> Unit,
    oraFine: String, onOraFineChange: (String) -> Unit,
    compenso: String, onCompensoChange: (String) -> Unit,
    onPubblica: () -> Unit
) {
    var showDatePicker by remember { mutableStateOf(false) }
    var showRangePicker by remember { mutableStateOf(false) }
    val allowedDays = remember { mutableStateListOf<DayOfWeek>() }

    val timeOptions = remember { (7..23).flatMap { h -> val hStr = if (h < 10) "0$h" else "$h"; listOf("$hStr:00", "$hStr:30") } + listOf("00:00", "00:30", "01:00", "01:30", "02:00", "02:30", "03:00", "03:30", "04:00", "04:30", "05:00", "05:30", "06:00", "06:30", "07:00", "07:30", "08:00", "08:30") }
    val rateOptions = remember { (25..100 step 5).map { "$it,00" } }

    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = Color.White)) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(stringResource(Res.string.publish_availability_caps), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(onClick = { showDatePicker = true }, modifier = Modifier.weight(1f)) { Icon(Icons.Default.Add, null); Spacer(Modifier.width(4.dp)); Text(stringResource(Res.string.single_date_caps), fontSize = 11.sp) }
                OutlinedButton(onClick = { showRangePicker = true }, modifier = Modifier.weight(1f)) { Icon(Icons.Default.DateRange, null); Spacer(Modifier.width(4.dp)); Text(stringResource(Res.string.interval_caps), fontSize = 11.sp) }
            }
            if (selectedDates.isNotEmpty()) {
                FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    selectedDates.sorted().forEach { millis ->
                        InputChip(selected = true, onClick = { selectedDates.remove(millis) }, label = { Text(TimeUtils.formatDate(Instant.fromEpochMilliseconds(millis), TimeZone.UTC), fontSize = 10.sp) }, trailingIcon = { Icon(Icons.Default.Close, null, modifier = Modifier.size(14.dp)) })
                    }
                    TextButton(onClick = { selectedDates.clear() }) { Text(stringResource(Res.string.clear_all_caps), fontSize = 10.sp, color = Color.Red) }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                EditableDropdownField(label = "Inizio", value = oraInizio, onValueChange = onOraInizioChange, options = timeOptions, modifier = Modifier.weight(1f))
                EditableDropdownField(label = "Fine", value = oraFine, onValueChange = onOraFineChange, options = timeOptions, modifier = Modifier.weight(1f))
            }
            EditableDropdownField(label = "Compenso Richiesto (€/h)", value = compenso, onValueChange = onCompensoChange, options = rateOptions, modifier = Modifier.fillMaxWidth(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal))
            Button(
                onClick = onPubblica,
                modifier = Modifier.fillMaxWidth(), enabled = selectedDates.isNotEmpty()
            ) { Text("PUBBLICA TUTTE LE DATE (${selectedDates.size})") }
        }
    }

    if (showRangePicker) {
        var tempStart by remember { mutableStateOf<Long?>(null) }
        var tempEnd by remember { mutableStateOf<Long?>(null) }
        AlertDialog(
            onDismissRequest = { showRangePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    if (tempStart != null && tempEnd != null) {
                        val generated = TimeUtils.generateDatesInRange(Instant.fromEpochMilliseconds(tempStart!!).toLocalDateTime(TimeZone.UTC).date, Instant.fromEpochMilliseconds(tempEnd!!).toLocalDateTime(TimeZone.UTC).date, allowedDays.toSet())
                        generated.forEach { val m = it.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds(); if (!selectedDates.contains(m)) selectedDates.add(m) }
                    }
                    showRangePicker = false
                }) { Text(stringResource(Res.string.generate_shifts_caps)) }
            },
            dismissButton = { TextButton(onClick = { showRangePicker = false }) { Text(stringResource(Res.string.cancel_button)) } },
            text = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Text(stringResource(Res.string.include_only_label), style = MaterialTheme.typography.labelSmall)
                    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                        val days = listOf(DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY, DayOfWeek.SUNDAY)
                        days.forEach { day ->
                            val isSelected = allowedDays.contains(day)
                            Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                                FilterChip(
                                    selected = isSelected, 
                                    onClick = { if (isSelected) allowedDays.remove(day) else allowedDays.add(day) }, 
                                    label = { 
                                        Text(
                                            text = when(day) { 
                                                DayOfWeek.MONDAY -> "L"; DayOfWeek.TUESDAY -> "M"; DayOfWeek.WEDNESDAY -> "M"
                                                DayOfWeek.THURSDAY -> "G"; DayOfWeek.FRIDAY -> "V"; DayOfWeek.SATURDAY -> "S"
                                                DayOfWeek.SUNDAY -> "D"; else -> "" 
                                            }, 
                                            fontSize = 10.sp,
                                            fontWeight = FontWeight.Bold
                                        ) 
                                    }, 
                                    modifier = Modifier.padding(horizontal = 1.dp).fillMaxWidth()
                                )
                            }
                        }
                    }
                    HorizontalCalendar(selectedDates = emptyList(), onDateSelected = {}, isRange = true, rangeStart = tempStart, rangeEnd = tempEnd, onRangeSelected = { s, e -> tempStart = s; tempEnd = e })
                }
            }
        )
    }

    if (showDatePicker) {
        AlertDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = { TextButton(onClick = { showDatePicker = false }) { Text(stringResource(Res.string.generate_shifts_caps)) } },
            text = {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Surface(color = Color.Red.copy(alpha = 0.1f), modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp), shape = RoundedCornerShape(8.dp)) {
                        Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(12.dp).background(Color.Red, RoundedCornerShape(2.dp)))
                            Spacer(Modifier.width(8.dp))
                            Text("Domeniche e festivi nazionali sono in Tariffa Festiva", style = MaterialTheme.typography.labelSmall, color = Color.Red)
                        }
                    }
                    HorizontalCalendar(selectedDates = selectedDates, onDateSelected = { if (selectedDates.contains(it)) selectedDates.remove(it) else selectedDates.add(it) })
                }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun FarmaciaAnnuncioContent(
    viewModel: AuthViewModel,
    selectedDates: androidx.compose.runtime.snapshots.SnapshotStateList<Long>,
    oraInizio: String, onOraInizioChange: (String) -> Unit,
    oraFine: String, onOraFineChange: (String) -> Unit,
    compenso: String, onCompensoChange: (String) -> Unit,
    pharmacistsNeeded: String, onPharmacistsNeededChange: (String) -> Unit,
    onPubblica: () -> Unit
) {
    var showDatePicker by remember { mutableStateOf(false) }
    var showRangePicker by remember { mutableStateOf(false) }
    val allowedDays = remember { mutableStateListOf<DayOfWeek>() }

    val timeOptions = remember { (7..23).flatMap { h -> val hStr = if (h < 10) "0$h" else "$h"; listOf("$hStr:00", "$hStr:30") } + listOf("00:00", "00:30", "01:00", "01:30", "02:00", "02:30", "03:00", "03:30", "04:00", "04:30", "05:00", "05:30", "06:00", "06:30", "07:00", "07:30", "08:00", "08:30") }
    val rateOptions = remember { (25..100 step 5).map { "$it,00" } }

    Card(modifier = Modifier.fillMaxWidth(), colors = CardDefaults.cardColors(containerColor = Color.White)) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(stringResource(Res.string.publish_shift_request_title), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(onClick = { showDatePicker = true }, modifier = Modifier.weight(1f)) { Icon(Icons.Default.Add, null); Spacer(Modifier.width(4.dp)); Text(stringResource(Res.string.single_date_caps), fontSize = 11.sp) }
                OutlinedButton(onClick = { showRangePicker = true }, modifier = Modifier.weight(1f)) { Icon(Icons.Default.DateRange, null); Spacer(Modifier.width(4.dp)); Text(stringResource(Res.string.interval_caps), fontSize = 11.sp) }
            }
            if (selectedDates.isNotEmpty()) {
                FlowRow(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    selectedDates.sorted().forEach { millis ->
                        InputChip(selected = true, onClick = { selectedDates.remove(millis) }, label = { Text(TimeUtils.formatDate(Instant.fromEpochMilliseconds(millis), TimeZone.UTC), fontSize = 10.sp) }, trailingIcon = { Icon(Icons.Default.Close, null, modifier = Modifier.size(14.dp)) })
                    }
                    TextButton(onClick = { selectedDates.clear() }) { Text(stringResource(Res.string.clear_all_caps), fontSize = 10.sp, color = Color.Red) }
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                EditableDropdownField(label = "Inizio", value = oraInizio, onValueChange = onOraInizioChange, options = timeOptions, modifier = Modifier.weight(1f))
                EditableDropdownField(label = "Fine", value = oraFine, onValueChange = onOraFineChange, options = timeOptions, modifier = Modifier.weight(1f))
            }
            EditableDropdownField(label = "Offerta Compenso (€/h)", value = compenso, onValueChange = onCompensoChange, options = rateOptions, modifier = Modifier.fillMaxWidth(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal))
            OutlinedTextField(value = pharmacistsNeeded, onValueChange = onPharmacistsNeededChange, label = { Text(stringResource(Res.string.pharmacists_needed)) }, modifier = Modifier.fillMaxWidth(), keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), shape = RoundedCornerShape(12.dp))
            Button(
                onClick = onPubblica,
                modifier = Modifier.fillMaxWidth(), enabled = selectedDates.isNotEmpty()
            ) { Text("PUBBLICA BLOCCO (${selectedDates.size * (pharmacistsNeeded.toIntOrNull() ?: 1)} turni)") }
        }
    }

    if (showRangePicker) {
        var tempStart by remember { mutableStateOf<Long?>(null) }
        var tempEnd by remember { mutableStateOf<Long?>(null) }
        AlertDialog(
            onDismissRequest = { showRangePicker = false },
            confirmButton = {
                TextButton(onClick = {
                    if (tempStart != null && tempEnd != null) {
                        val generated = TimeUtils.generateDatesInRange(Instant.fromEpochMilliseconds(tempStart!!).toLocalDateTime(TimeZone.UTC).date, Instant.fromEpochMilliseconds(tempEnd!!).toLocalDateTime(TimeZone.UTC).date, allowedDays.toSet())
                        generated.forEach { val m = it.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds(); if (!selectedDates.contains(m)) selectedDates.add(m) }
                    }
                    showRangePicker = false
                }) { Text(stringResource(Res.string.generate_shifts_caps)) }
            },
            dismissButton = { TextButton(onClick = { showRangePicker = false }) { Text(stringResource(Res.string.cancel_button)) } },
            text = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Text(stringResource(Res.string.include_only_label), style = MaterialTheme.typography.labelSmall)
                    Row(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
                        val days = listOf(DayOfWeek.MONDAY, DayOfWeek.TUESDAY, DayOfWeek.WEDNESDAY, DayOfWeek.THURSDAY, DayOfWeek.FRIDAY, DayOfWeek.SATURDAY, DayOfWeek.SUNDAY)
                        days.forEach { day ->
                            val isSelected = allowedDays.contains(day)
                            Box(modifier = Modifier.weight(1f), contentAlignment = Alignment.Center) {
                                FilterChip(
                                    selected = isSelected, 
                                    onClick = { if (isSelected) allowedDays.remove(day) else allowedDays.add(day) }, 
                                    label = { 
                                        Text(
                                            text = when(day) { 
                                                DayOfWeek.MONDAY -> "L"; DayOfWeek.TUESDAY -> "M"; DayOfWeek.WEDNESDAY -> "M"
                                                DayOfWeek.THURSDAY -> "G"; DayOfWeek.FRIDAY -> "V"; DayOfWeek.SATURDAY -> "S"
                                                DayOfWeek.SUNDAY -> "D"; else -> "" 
                                            }, 
                                            fontSize = 10.sp,
                                            fontWeight = FontWeight.Bold
                                        ) 
                                    }, 
                                    modifier = Modifier.padding(horizontal = 1.dp).fillMaxWidth()
                                )
                            }
                        }
                    }
                    HorizontalCalendar(selectedDates = emptyList(), onDateSelected = {}, isRange = true, rangeStart = tempStart, rangeEnd = tempEnd, onRangeSelected = { s, e -> tempStart = s; tempEnd = e })
                }
            }
        )
    }

    if (showDatePicker) {
        AlertDialog(
            onDismissRequest = { showDatePicker = false },
            confirmButton = { TextButton(onClick = { showDatePicker = false }) { Text(stringResource(Res.string.generate_shifts_caps)) } },
            text = {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Surface(color = Color.Red.copy(alpha = 0.1f), modifier = Modifier.fillMaxWidth().padding(bottom = 8.dp), shape = RoundedCornerShape(8.dp)) {
                        Row(modifier = Modifier.padding(8.dp), verticalAlignment = Alignment.CenterVertically) {
                            Box(modifier = Modifier.size(12.dp).background(Color.Red, RoundedCornerShape(2.dp)))
                            Spacer(Modifier.width(8.dp))
                            Text("Domeniche e festivi nazionali sono in Tariffa Festiva", style = MaterialTheme.typography.labelSmall, color = Color.Red)
                        }
                    }
                    HorizontalCalendar(selectedDates = selectedDates, onDateSelected = { if (selectedDates.contains(it)) selectedDates.remove(it) else selectedDates.add(it) })
                }
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EditAnnuncioDialog(annuncio: AnnuncioTurno, onDismiss: () -> Unit, onSave: (AnnuncioTurno) -> Unit) {
    var oraInizio by remember { mutableStateOf(annuncio.oraInizio) }
    var oraFine by remember { mutableStateOf(annuncio.oraFine) }
    var compenso by remember { mutableStateOf(TimeUtils.formatCurrency(annuncio.compenso ?: 0.0)) }
    var quantitaMax by remember { mutableStateOf(annuncio.quantitaMassima.toString()) }
    val timeOptions = remember { (7..23).flatMap { h -> val hStr = if (h < 10) "0$h" else "$h"; listOf("$hStr:00", "$hStr:30") } + listOf("00:00", "00:30") }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Modifica Annuncio") },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                EditableDropdownField(label = "Inizio (HH:mm)", value = oraInizio, onValueChange = { oraInizio = it }, options = timeOptions)
                EditableDropdownField(label = "Fine (HH:mm)", value = oraFine, onValueChange = { oraFine = it }, options = timeOptions)
                OutlinedTextField(value = compenso, onValueChange = { compenso = it }, label = { Text("Compenso (€/h)") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal), shape = RoundedCornerShape(12.dp))
                if (annuncio.creatoDa == CreatoreTipo.FARMACIA) {
                    OutlinedTextField(value = quantitaMax, onValueChange = { quantitaMax = it }, label = { Text("Farmacisti richiesti") }, keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number), shape = RoundedCornerShape(12.dp))
                }
            }
        },
        confirmButton = {
            Button(onClick = {
                val localDate = annuncio.dataTurno.toLocalDateTime(TimeZone.currentSystemDefault()).date
                onSave(annuncio.copy(oraInizio = oraInizio, oraFine = oraFine, compenso = TimeUtils.parseCurrency(compenso), tariffaTipo = TimeUtils.calculateTariffa(localDate, oraInizio), quantitaMassima = quantitaMax.toIntOrNull() ?: 1))
            }) { Text("SALVA") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("ANNULLA") } }
    )
}
